import React from 'react';
import SettingsIcon from '@material-ui/icons/Settings';
import DashboardIcon from '@material-ui/icons/Dashboard';
import TableIcon from '@material-ui/icons/GridOn';
import FormIcon from '@material-ui/icons/Tune';
import BarChartIcon from '@material-ui/icons/BarChart';
import TextFieldEditor from '../components/editors/TextFieldEditor';
import SliderEditor from '../components/editors/SliderEditor';
import ArrayEditor from '../components/editors/ArrayEditor';
import EnumEditor from '../components/editors/EnumEditor';
import ShortStringEditor from '../components/editors/ShortStringEditor';
import DurationEditor from '../components/editors/DurationEditor';
import DateEditor from '../components/editors/DateEditor';
import BooleanEditor from '../components/editors/BooleanEditor';
import {
  UnicityValidator,
  UNICITY_VALIDATOR_KEY,
  EmptyValidator,
  EMPTY_VALIDATOR,
} from './Validators';
import {
  FixedDigitsFormatter,
  FIXED_DIGITS_FORMATTER_KEY,
  PercentageFormatter,
  PERCENTAGE_FORMATTER_KEY,
  DollarsFormatter,
  DOLLARS_FORMATTER_KEY,
  DateFormatter,
  DATE_FORMATTER_KEY,
  DurationFormatter,
  DURATION_FORMATTER_KEY,
} from './Formatters';

const APP_TYPE = 'App';
const APP_MAIN_TYPE = 'AppMain';
const APP_NAV_DRAWER_TYPE = 'NavDrawer';
const APP_NAV_DRAWER_HEADER_TYPE = 'NavDrawerHeader';
const APP_TOOLBAR_TYPE = 'AppToolbar';
const APP_TOOLBAR_LEADING_CONTENT_TYPE = 'AppToolbarLeadingContent';
const APP_TOOLBAR_TRAILING_CONTENT_TYPE = 'AppToolbarTrailingContent';
const APP_TOOLBAR_CONTENT_TYPE = 'AppToolbarContent';
const LOGIN_COMPONENT_TYPE = 'Login';
const TABLE_VIEW_TYPE = 'Table';
const TABLE_ROW_TYPE = 'TableRow';
const FORM_VIEW_TYPE = 'Form';
const RADAR_CHART_VIEW_TYPE = 'Radar';
const BAR_CHART_VIEW_TYPE = 'BarChart';
const DOCUMENTATION_API_VIEW_TYPE = 'API';
const HTML_VIEW_TYPE = 'HTML';
const EDIT_PROFILE_TYPE = 'EditProfile';
const SEARCH_BAR_TYPE = 'SearchBar';
const IDLE_DIALOG_TYPE = 'IdleDialog';

const STRING_EDITOR_TYPE = 'string';
const INTEGER_EDITOR_TYPE = 'integer';
const NUMBER_EDITOR_TYPE = 'number';
const PERCENTAGE_TYPE = 'percentage';
const ARRAY_EDITOR_TYPE = 'array';
const ENUM_EDITOR_TYPE = 'enum';
const SHORT_STRING_TYPE = 'short_string';
const DURATION_TYPE = 'duration';
const DATE_TYPE = 'date';
const BOOLEAN_TYPE = 'boolean';

class ViewService {
  constructor(config) {
    this.schemaService = config.schemaService;
    this.viewComps = {};

    this.iconsByName = {};
    this.iconsByName.GridOn = TableIcon;
    this.iconsByName.Tune = FormIcon;
    this.iconsByName.BarChart = BarChartIcon;
    this.iconsByName.Settings = SettingsIcon;
    this.iconsByName.Dashboard = DashboardIcon;

    this.editors = {};
    this.validatorsByType = {};
    this.registerEditor(STRING_EDITOR_TYPE, new TextFieldEditor('string'));
    this.registerEditor(INTEGER_EDITOR_TYPE, new TextFieldEditor('int'));
    this.registerEditor(NUMBER_EDITOR_TYPE, new TextFieldEditor('number'));
    this.registerEditor(PERCENTAGE_TYPE, new SliderEditor('percentage', null, {
      min: { value: 0, message: 'Percentage value cannot be lower than 0' },
      max: { value: 100, message: 'Percentage value cannot be greater than 100' },
    }));
    this.registerEditor(ARRAY_EDITOR_TYPE, new ArrayEditor());
    this.registerEditor(ENUM_EDITOR_TYPE, new EnumEditor(this.schemaService));
    this.registerEditor(SHORT_STRING_TYPE, new ShortStringEditor());
    this.registerEditor(DURATION_TYPE, new DurationEditor());
    this.registerEditor(DATE_TYPE, new DateEditor());
    this.registerEditor(BOOLEAN_TYPE, new BooleanEditor());

    this.validatorsByName = Object.assign({
      [UNICITY_VALIDATOR_KEY]: UnicityValidator,
      [EMPTY_VALIDATOR]: EmptyValidator,
    });

    this.formattersByName = Object.assign({
      [FIXED_DIGITS_FORMATTER_KEY]: FixedDigitsFormatter,
      [PERCENTAGE_FORMATTER_KEY]: PercentageFormatter,
      [DOLLARS_FORMATTER_KEY]: DollarsFormatter,
      [DATE_FORMATTER_KEY]: DateFormatter,
      [DURATION_FORMATTER_KEY]: DurationFormatter,
    });

    this.computedProperties = {};

    this.viewHooks = {};
  }

  //
  // Icons
  //
  getIcon(iconName) {
    const icon = iconName && this.iconsByName[iconName];
    if (iconName && !icon) {
      console.log(`Error: cannot find icon: ${iconName}. Use 'icons' declaration in your configuration to register new icons`);
    }
    return icon;
  }

  registerIcon(name, icon) {
    this.iconsByName[name] = icon;
  }

  //
  // Validators
  //
  /**
   * Converts references to validators with validator functions.
   * @param {validators} Array of references to validators.
   */
  processValidators(validators, type, propertyName, propertyType) {
    function makeValidatorFromObject(fct, obj) {
      const copy = Object.assign({}, obj);
      delete copy.name;
      return (value, data, pName, ctx) => {
        return fct(value, data, pName, Object.assign({}, ctx, copy));
      };
    }
    let processedValidators = (validators || []).map((validator) => {
      if (typeof validator === 'string') {
        const defaultValidator = this.validatorsByName[validator];
        if (!defaultValidator) {
          this.error(`Validator ${validator} has not been registered. Declare a validators { ... } entry in your Javascript configuration file.`);
          return null;
        }
        return defaultValidator;
      }
      if (typeof validator === 'function') {
        return validator;
      }
      // Validator is an object declaring parameters to pass to the validator function
      // Compile it into a function
      if (!validator.name) {
        this.error(`Validator ${JSON.stringify(validator)} does not provide a name property for the name of the validator to use, like 'unicity'`);
        return null;
      }
      const validatorFct = this.validatorsByName[validator.name];
      if (!validatorFct) {
        this.error(`Unknow validator ${validator.name}. Declare a validator in the validators { ... } entry in your Javascript configuration file.`);
        return null;
      }
      return makeValidatorFromObject(validatorFct, validator);
    }).filter(v => v);
    if (propertyType) {
      const defaultValidators = this.validatorsByType[propertyType];
      if (defaultValidators) {
        processedValidators.push(...defaultValidators);
      }
    }
    if (type) {
      // Add validators registered by type
      for (let typeIndex = 0; typeIndex < this.validatorsBySchemaType.length; typeIndex += 2) {
        if (this.validatorsBySchemaType[typeIndex] === type) {
          const validatorsByProps = this.validatorsBySchemaType[typeIndex + 1];
          const propValidators = validatorsByProps[propertyName];
          if (propValidators) {
            processedValidators = processedValidators.concat(propValidators);
          }
          break;
        }
      }
    }
    return processedValidators;
  }

  registerValidators(configuration, props) {
    // Process validators from jsonConfig
    const { jsonConfig } = props;
    const { schemaService } = configuration;
    this.validatorsBySchemaType = [];
    const typeValidators = jsonConfig.validators;
    if (schemaService && typeValidators) {
      Object.keys(typeValidators).forEach((typeName) => {
        const schemaType = schemaService.getType(typeName);
        if (!schemaType) {
          this.error(`Unkown schema type ${typeName}`);
        } else {
          const validatorsByProp = {};
          const propValidators = typeValidators[typeName];
          Object.keys(propValidators).forEach((propName) => {
            validatorsByProp[propName] = this.processValidators(propValidators[propName]);
          });
          this.validatorsBySchemaType.push(schemaType, validatorsByProp);
        }
      });
    }
  }

  registerValidator(name, validator) {
    this.validatorsByName[name] = validator;
  }

  //
  // Formatters
  //
  processFormatters(formatters, type, propertyName) {
    function makeFormatterFromObject(fct, obj) {
      const copy = Object.assign({}, obj);
      delete copy.name;
      return (value, data, pName, ctx) => {
        return fct(value, data, pName, Object.assign({}, ctx, copy));
      };
    }
    let processedFormatters;
    if (!formatters) {
      processedFormatters = [];
    } else {
      processedFormatters = (Array.isArray(formatters) ? formatters : [formatters]).map((formatter) => {
        if (typeof formatter === 'string') {
          const defaultFormatter = this.formattersByName[formatter];
          if (!defaultFormatter) {
            this.error(`Formatter ${formatter} has not been registered. Declare a formatters { ... } entry in your Javascript configuration file.`);
            return null;
          }
          return defaultFormatter;
        }
        if (typeof formatter === 'function') {
          return formatter;
        }
        // Formatter is an object declaring parameters to pass to the formatter function
        // Compile it into a function
        if (!formatter.name) {
          this.error(`Formatter ${JSON.stringify(formatter)} does not provide a name property for the name of the formatter to use, like 'unicity'`);
          return null;
        }
        const formatterFct = this.formattersByName[formatter.name];
        if (!formatterFct) {
          this.error(`Unknow formatter ${formatter.name}. Declare a formatter in the formatters { ... } entry in your Javascript configuration file.`);
          return null;
        }
        return makeFormatterFromObject(formatterFct, formatter);
      }).filter(v => v);
    }
    // Add formatters registered by type
    for (let typeIndex = 0; typeIndex < this.formattersByType.length; typeIndex += 2) {
      if (this.formattersByType[typeIndex] === type) {
        const formattersByProps = this.formattersByType[typeIndex + 1];
        const propFormatters = formattersByProps[propertyName];
        if (propFormatters) {
          processedFormatters = processedFormatters.concat(propFormatters);
        }
        break;
      }
    }
    if (processedFormatters.length === 0) {
      return null;
    }
    if (processedFormatters.length === 1) {
      return processedFormatters[0];
    }
    return (value, data, property, context) => {
      // let fValue = value;
      // for (let i = 0; i < processedFormatters.length; i += 1) {
      //   fValue = processedFormatters[i](fValue, data, prop.name, context);
      // }
      return processedFormatters[0](value, data, property, context);
    };
  }

  registerFormatters(configuration, props) {
    const { jsonConfig } = props;
    const { schemaService } = configuration;

    // Process formatters from jsonConfig
    this.formattersByType = [];
    const typeFormatters = jsonConfig.formatters;
    if (typeFormatters) {
      Object.keys(typeFormatters).forEach((typeName) => {
        const schemaType = schemaService.getType(typeName);
        if (!schemaType) {
          this.error(`Unkown schema type ${typeName}`);
        } else {
          const formattersByProp = {};
          const valueFormatters = typeFormatters[typeName];
          Object.keys(valueFormatters).forEach((propName) => {
            formattersByProp[propName] = this.processFormatters(valueFormatters[propName]);
          });
          this.formattersByType.push(schemaType, formattersByProp);
        }
      });
    }
  }

  registerFormatter(name, formatter) {
    this.formattersByName[name] = formatter;
  }

  //
  // Properties
  //
  /**
   * Augment data properties with visualization properties such as formatters and validators.
   * @param {*} viewProps Properties declared in the JSON configuration file for the view.
   * @param {*} dataProps Properties as provided with the data of the view.
   * @param {*} propsByName Hash map to store properties by their name.
   * @param {*} properties Stores processed data props.
   * @param {*} hook View hook if associated with the view.
   * @param {*} hookViewPropertiesMethod View hook method for customizing view properties.
   */
  computeViewProperties(viewProps, dataProps, propsByName = {}, properties, hook = null, hookViewPropertiesMethod = null, onError) {
    let mutDataProps = dataProps;
    if (hook && hook.getProperties) {
      mutDataProps = hook.getProperties(dataProps);
    }
    let mutViewProps = viewProps || mutDataProps.concat([]);
    const mutPropsByName = propsByName;
    if (hook && hook[hookViewPropertiesMethod]) {
      mutViewProps = this.hook[hookViewPropertiesMethod](mutViewProps);
    }
    mutDataProps.forEach((prop) => {
      mutPropsByName[prop.name] = prop;
      if (properties) {
        properties.push(prop);
      }
    });
    mutViewProps = mutViewProps.map((viewProp) => {
      let prop = mutPropsByName[viewProp.name];
      let propName;
      let type;
      if (!prop) {
        if (this.computedProperties[viewProp.name]) {
          prop = Object.assign({}, this.computedProperties[viewProp.name], viewProp);
          mutPropsByName[viewProp.name] = prop;
          prop.validators = this.processValidators(prop.validators, type, propName, prop.type);
          prop.formatValue = this.processFormatters(prop.formatters, type, propName);
        } else if (viewProp.name.indexOf('.') >= 0) {
          const propArray = viewProp.name.split('.');
          prop = mutPropsByName[propArray[0]];
          let index = 1;
          let editable = prop ? prop.editable : false;
          while (prop && (index < propArray.length)) {
            propName = propArray[index];
            type = this.schemaService.getType(prop.type);
            prop = type && type.properties ? type.properties[propName] : null;
            editable = prop && (editable === undefined || editable) ? prop.editable : false;
            index += 1;
          }
          if (prop) {
            prop = Object.assign({}, prop, viewProp, { editable });
            mutPropsByName[viewProp.name] = prop;
            if (!prop.getValue) {
              prop.getValue = (obj) => {
                let iProp = 0;
                let value = obj;
                while (value && (iProp < propArray.length)) {
                  value = value[propArray[iProp]];
                  iProp += 1;
                }
                return value;
              };
            }
            prop.validators = this.processValidators(prop.validators, type, propName, prop.type);
            prop.formatValue = this.processFormatters(prop.formatters, type, propName);
          }
        } else {
          prop = viewProp;
          if (prop.type) {
            prop.validators = this.processValidators(prop.validators, type, propName, prop.type);
            prop.formatValue = this.processFormatters(prop.formatters, type, propName);
          }
        }
      } else {
        prop = Object.assign({}, prop, viewProp);
      }
      if (!prop && onError) {
        onError(`Property name ${viewProp.name} has no matching property`);
      }
      return prop;
    });
    return mutViewProps;
  }

  initalizeDataProperties(data, type, keepUserPropertySet) {
    const schemaPropsByName = {};
    let schemaProperties = this.schemaService.getPropertyNames(type).map((name) => {
      const prop = type.properties[name];
      const schemaProp = {
        name,
        label: prop.label || name,
        type: prop.type,
        description: prop.description,
      };
      // schemaPropsByName[name] = schemaProp;
      return schemaProp;
    });
    if (data.properties) {
      const viewProperties = this.computeViewProperties(
        data.properties,
        schemaProperties,
        schemaPropsByName,
        undefined,
        null,
        null,
        this.onError,
      );
      if (keepUserPropertySet) {
        schemaProperties = viewProperties;
      }
    }
    // Process validators and formatters
    schemaProperties.forEach((schemaProp) => {
      const p = schemaProp;
      if (!p.validators) { // Otherwise done in computeViewProperties
        p.validators = this.processValidators(schemaProp.validators, type, schemaProp.name);
        p.formatValue = this.processFormatters(schemaProp.formatters, type, schemaProp.name);
      }
    });
    return schemaProperties;
  }

  initializeDataConfiguration(dataCfgs) {
    dataCfgs.forEach((dataCfg) => {
      const d = dataCfg; // Workaround eslint rule for editing props of function param
      // If data points to a path from the swagger def, the schema is the get/result/sceham from that swagger path
      // Otherwise, if data declares a type, the schema is the data
      const schema = d.path ? this.schemaService.getPathSchema(d.path) : (d.type && d || null);
      if (schema) {
        let type = this.schemaService.getType(schema.type);
        if (schema.isArray) {
          if (!d.id) {
            d.id = `${d.path}-array`;
          }
          d.properties = this.initalizeDataProperties(d, type);
          d.description = type.description;
          d.type = 'array';
        } else if (d.property) {
          if (!d.id) {
            d.id = `${d.path}-${d.property}`;
          }
          const singletonProp = type.properties[d.property];
          if (singletonProp) {
            type = this.schemaService.getType(singletonProp.type);
            d.properties = this.initalizeDataProperties(d, type);
            d.description = type.description;
            d.type = singletonProp.type;
          }
        } else {
          if (!d.id) {
            d.id = d.path;
          }
          d.type = type;
          d.description = type.description;
          d.properties = this.initalizeDataProperties(d, type);
        }
      }
    });
  }

  //
  // Editors
  //
  registerEditor(...params) {
    const Editor = params[params.length - 1];
    const type = params.length === 2 ? params[0] : Editor.name;
    this.editors[type] = Editor;
    const validators = Editor.getDefaultValidators && Editor.getDefaultValidators();
    if (validators && validators.length) {
      this.validatorsByType[type] = validators;
    }
  }

  getEditor(type) {
    let editor = this.editors[type];
    if (!editor) {
      const schemaType = this.schemaService.getType(type);
      if (schemaType && this.schemaService.isEnum(schemaType)) {
        editor = new EnumEditor(this.schemaService, type);
        this.editors[type] = editor;
        return editor;
      }
      console.error(`No editor found for type ${type}`);
    }
    return editor;
  }

  //
  // View components
  //
  registerComponent(type, Component) {
    const compDef = this.viewComps[type];
    const previousComponent = compDef ? compDef.component : undefined;
    if (!Component) {
      this.viewComps[type] = { component: undefined };
    } else {
      // Content must always be provided, even if null, otherwise Component must be provided with the Content of a component higher in the component chain.
      this.viewComps[type] = { component: props => <Component {...props} Content={previousComponent} /> };
    }
  }

  registerComponentInfo(type, compInfo) {
    const prevInfo = this.viewComps[type];
    this.viewComps[type] = Object.assign({}, prevInfo, compInfo);
    if (prevInfo && prevInfo.component && compInfo.component) {
      const PrevComp = prevInfo.component;
      const Component = compInfo.component;
      this.viewComps[type].component = props => <Component {...props} Content={PrevComp} />;
    }
  }

  registerViewComponent(type, compDecl) {
    this.viewComps[type] = compDecl;
    if (compDecl.multiData === undefined) {
      this.viewComps[type].multiData = true;
    }
  }

  isViewComponentMultiData(type) {
    return this.viewComps[type].multiData;
  }

  getViewComponent(type) {
    return this.viewComps[type].component;
  }

  getComponent(type) {
    if (typeof type === 'string') {
      const cmdDef = this.viewComps[type];
      return cmdDef && cmdDef.component;
    }
    return type;
  }

  getComponentInfo(type) {
    return this.viewComps[type];
  }

  getViewIcon(type) {
    return this.viewComps[type].icon;
  }

  getViewComponentConfigProcessor(type) {
    const compDecl = this.viewComps[type];
    if (compDecl && compDecl.initializeConfiguration) {
      return compDecl.initializeConfiguration.bind(compDecl);
    }
    return null;
  }

  //
  // View customizers
  //

  registerViewHook(viewId, hook) {
    this.viewHooks[viewId] = hook;
  }

  getViewHookClass(viewId) {
    return this.viewHooks[viewId];
  }

  // Clear
  clearFormattersAndValidators() {
    // Method called after validators and formatters have been initializes in data properties
    delete this.validatorsByName;
    delete this.validatorsBySchemaType;
    delete this.formattersByName;
    delete this.formattersByType;
  }

  //
  // Computed properties
  //
  registerComputedProperty(propName, property) {
    this.computedProperties[propName] = property;
  }

  //
  // Errors
  //
  error(msg) {
    if (this.onError) {
      this.onError(new Error(msg));
    } else {
      (console.error || console.log)(msg);
    }
  }

  setErrorHandler(onError) {
    this.onError = onError;
  }
}

const extendViewService = (viewService, extension) => {
  const extViewService = Object.create(viewService);
  if (extension.iconsByName) {
    extViewService.iconsByName = Object.create(viewService.iconsByName);
    Object.assign(extViewService.iconsByName, extension.iconsByName);
  }
  if (extension.editors) {
    extViewService.editors = Object.create(viewService.editors);
    extViewService.validatorsByType = Object.create(viewService.validatorsByType);
    extension.editors.forEach((editor) => { extViewService.registerEditor(editor); });
  }
  if (extension.componentsByName) {
    extViewService.viewComps = Object.create(viewService.viewComps);
    Object.keys(extension.componentsByName).forEach((key) => {
      extViewService.registerComponent(key, extension.componentsByName[key]);
    });
  }
  return extViewService;
};

export {
  APP_TYPE,
  APP_MAIN_TYPE,
  APP_NAV_DRAWER_TYPE,
  APP_NAV_DRAWER_HEADER_TYPE,
  APP_TOOLBAR_TYPE,
  APP_TOOLBAR_CONTENT_TYPE,
  APP_TOOLBAR_LEADING_CONTENT_TYPE,
  APP_TOOLBAR_TRAILING_CONTENT_TYPE,
  LOGIN_COMPONENT_TYPE,
  TABLE_VIEW_TYPE,
  TABLE_ROW_TYPE,
  RADAR_CHART_VIEW_TYPE,
  BAR_CHART_VIEW_TYPE,
  FORM_VIEW_TYPE,
  DOCUMENTATION_API_VIEW_TYPE,
  HTML_VIEW_TYPE,
  EDIT_PROFILE_TYPE,
  SEARCH_BAR_TYPE,
  IDLE_DIALOG_TYPE,
  ViewService,
  extendViewService,
};
